import { BarcodeScanError, type BarcodeScanner, type BarcodeScanOptions } from "./barcode-scanner";
import { normalizeBarcode } from "../food-sources/open-food-facts/mapper";

interface DetectedBarcode { rawValue: string }
interface Detector {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
interface DetectorConstructor {
  new(options?: { formats?: string[] }): Detector;
  getSupportedFormats?(): Promise<string[]>;
}

export class BrowserBarcodeDetectorScanner implements BarcodeScanner {
  async isSupported(): Promise<boolean> {
    try {
      const constructor = getDetectorConstructor();
      if (!constructor || !navigator.mediaDevices?.getUserMedia) return false;
      if (!constructor.getSupportedFormats) return true;
      const formats = await constructor.getSupportedFormats();
      return formats.some((format) => SUPPORTED_FORMATS.includes(format));
    } catch {
      return false;
    }
  }

  async scan(video: HTMLVideoElement, options: BarcodeScanOptions = {}): Promise<string> {
    const DetectorClass = getDetectorConstructor();
    if (!DetectorClass || !await this.isSupported()) {
      throw new BarcodeScanError("Native barcode scanning is unavailable", "unsupported");
    }
    const timeout = AbortSignal.timeout(options.timeoutMs ?? 60_000);
    const signal = combineSignals(options.signal, timeout);
    let stream: MediaStream | undefined;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      await video.play();
      const formats = DetectorClass.getSupportedFormats ? await DetectorClass.getSupportedFormats() : SUPPORTED_FORMATS;
      const detector = new DetectorClass({ formats: formats.filter((format) => SUPPORTED_FORMATS.includes(format)) });
      return await detectUntilFound(detector, video, signal);
    } catch (error) {
      if (error instanceof BarcodeScanError) throw error;
      if (signal.aborted) throw signal.reason ?? new DOMException("Aborted", "AbortError");
      if (error instanceof DOMException && ["NotAllowedError", "SecurityError"].includes(error.name)) {
        throw new BarcodeScanError("Camera permission was not granted", "permission-denied");
      }
      throw new BarcodeScanError("The camera could not scan this barcode", "camera-error");
    } finally {
      stopCamera(video, stream);
    }
  }
}

const SUPPORTED_FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "itf"];

async function detectUntilFound(detector: Detector, video: HTMLVideoElement, signal: AbortSignal): Promise<string> {
  while (!signal.aborted) {
    const results = await detector.detect(video);
    for (const result of results) {
      const barcode = normalizeBarcode(result.rawValue);
      if (barcode) return barcode;
    }
    await nextFrame(signal);
  }
  throw signal.reason ?? new DOMException("Aborted", "AbortError");
}

function nextFrame(signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const frame = requestAnimationFrame(() => resolve());
    signal.addEventListener("abort", () => {
      cancelAnimationFrame(frame);
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
    }, { once: true });
  });
}

function getDetectorConstructor(): DetectorConstructor | undefined {
  return (globalThis as typeof globalThis & { BarcodeDetector?: DetectorConstructor }).BarcodeDetector;
}

export function stopCamera(video: HTMLVideoElement, stream?: MediaStream): void {
  const attached = stream ?? (video.srcObject instanceof MediaStream ? video.srcObject : undefined);
  attached?.getTracks().forEach((track) => track.stop());
  video.pause();
  video.srcObject = null;
  video.removeAttribute("src");
}

function combineSignals(...signals: Array<AbortSignal | undefined>): AbortSignal {
  const available = signals.filter((signal): signal is AbortSignal => Boolean(signal));
  if (typeof AbortSignal.any === "function") return AbortSignal.any(available);
  const controller = new AbortController();
  for (const signal of available) {
    if (signal.aborted) controller.abort(signal.reason);
    else signal.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}
